/*
  Studio Pro By Charlot · l'apercu de l'etiquette, qui sert de bon a tirer.

  Il passe par l'API d'etiquettes du site (api.bycharlot.akairos.fr), la meme qui
  sert au configurateur des fiches produits et a la production :
    1. file/tmp        depose le logo et rend son empreinte (FileHash)
    2. label/compute   met en page le texte pour une taille donnee et rend une cle
    3. label/preview   rend l'etiquette imprimee (vraie police, logo place) en PNG

  Differences voulues avec le client du theme :
  - on ne touche jamais au stockage du navigateur et on ne recharge jamais la page
    (le theme efface tout et recharge sur une erreur 403 : la selection du pro
    serait perdue) ;
  - chaque appel a un delai maximal ; en cas d'echec, l'appelant affiche un
    apercu simplifie et le bon a tirer definitif part avec le devis.
*/
(function (racine, fabrique) {
  if (typeof module === "object" && module.exports) module.exports = fabrique();
  else racine.StudioEtiquette = fabrique();
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";

  const HOTE = "https://api.bycharlot.akairos.fr";
  const VERSION = "v1";
  const CLIENT = "ByCharlot-Shopify-v1";
  const LANGUE = "fr_FR";
  const DELAI = 12000;
  const LOGO_MAX = 1200;          // px, cote le plus long apres reduction
  const session = Array.from(crypto.getRandomValues(new Uint8Array(16)), b => b.toString(16).padStart(2, "0")).join("");

  // meme serialisation que le theme : cles triees, en minuscules, valeurs concatenees
  function aplatir(o) {
    let s = "";
    for (const k of Object.keys(o).sort()) {
      s += k.toLowerCase();
      const v = o[k];
      if (v !== null && typeof v === "object") {
        s += Array.isArray(v) ? v.map(x => (x !== null && typeof x === "object" ? aplatir(x) : String(x))).join(",") : aplatir(v);
      } else s += String(v);
    }
    return s;
  }

  async function signer(chemin, entetes, corps) {
    const brut = chemin + entetes["bycharlot-session"] + entetes["bycharlot-timestamp"] + entetes["bycharlot-lang"] + "" + (corps ? aplatir(corps) : "");
    const h = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(brut));
    return Array.from(new Uint8Array(h), b => b.toString(16).padStart(2, "0")).join("");
  }

  async function appeler(chemin, corps) {
    const entetes = {
      "Content-Type": "application/json", "bycharlot-client": CLIENT, "bycharlot-lang": LANGUE,
      "bycharlot-session": session, "bycharlot-timestamp": String(Math.round(Date.now() / 1000)), "bycharlot-install-id": ""
    };
    entetes["bycharlot-token"] = await signer(`/${VERSION}/${chemin}`, entetes, corps);
    const arret = new AbortController();
    const minuteur = setTimeout(() => arret.abort(), DELAI);
    try {
      const r = await fetch(`${HOTE}/${VERSION}/${chemin}`, { method: "POST", headers: entetes, body: JSON.stringify(corps), signal: arret.signal });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return await r.json();
    } finally { clearTimeout(minuteur); }
  }

  // reduit l'image dans le navigateur : un logo de 15 Mo part en quelques centaines de Ko
  function reduire(fichier) {
    return new Promise((ok, ko) => {
      if (!/^image\/(png|jpeg)$/.test(fichier.type)) return ko(new Error("format"));
      const url = URL.createObjectURL(fichier), img = new Image();
      img.onload = () => {
        const f = Math.min(1, LOGO_MAX / Math.max(img.naturalWidth, img.naturalHeight));
        const c = document.createElement("canvas");
        c.width = Math.max(1, Math.round(img.naturalWidth * f)); c.height = Math.max(1, Math.round(img.naturalHeight * f));
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        URL.revokeObjectURL(url);
        ok({ dataURL: c.toDataURL("image/png"), largeur: img.naturalWidth, hauteur: img.naturalHeight });
      };
      img.onerror = () => { URL.revokeObjectURL(url); ko(new Error("illisible")); };
      img.src = url;
    });
  }

  async function deposerLogo(fichier) {
    const r = await reduire(fichier);
    const d = await appeler("file/tmp", { filename: "", fileContent: r.dataURL });
    if (!d || !d.FileHash) throw new Error("empreinte absente");
    return { empreinte: d.FileHash, dataURL: r.dataURL, largeur: r.largeur, hauteur: r.hauteur, petit: Math.max(r.largeur, r.hauteur) < 300 };
  }

  // les quatre etiquettes du site :
  //   texte        titre + sous-titre (3 lignes chacun)       <T>-ETI-PERS
  //   logo-titre   logo + titre (2 lignes)                    <T>-ETI-PERS#IMAGE-TITLE
  //   logo-texte   logo + petit texte sous le logo (2 lignes)  <T>-ETI-PERS#IMAGE-TEXT
  //   logo         logo seul                                  <T>-ETI-PERS#IMAGE-ONLY
  const TYPES = { "texte": "", "logo-titre": "#IMAGE-TITLE", "logo-texte": "#IMAGE-TEXT", "logo": "#IMAGE-ONLY" };
  function skuEtiquette(taille, titre, empreinte, type) {
    if (type && type in TYPES) return `${taille}-ETI-PERS${type === "texte" || !empreinte ? "" : TYPES[type]}`;
    if (!empreinte) return `${taille}-ETI-PERS`;
    return `${taille}-ETI-PERS#${titre ? "IMAGE-TITLE" : "IMAGE-ONLY"}`;
  }

  // taille : XS, S, M ; renvoie la cle d'apercu et le texte tel qu'il sera imprime
  async function mettreEnPage({ taille, titre, sousTitre, empreinte, type }) {
    const sku = skuEtiquette(taille, titre, empreinte, type);
    // chaque gabarit n'imprime que ses champs : on n'envoie pas ce qu'il ne sait pas afficher
    if (/IMAGE-ONLY$/.test(sku)) { titre = ""; sousTitre = ""; }
    if (/IMAGE-TITLE$/.test(sku)) sousTitre = "";
    if (/IMAGE-TEXT$/.test(sku)) titre = "";
    const donnees = { count: 1, labelSize: taille, rawTitle: titre || "", rawSubtitle: sousTitre || "", computeSubtitleFirst: true, labelSku: sku };
    if (empreinte) donnees.imageHash = empreinte;
    const d = await appeler("label/compute", { customData: donnees });
    const c = (d && d.customData) || {};
    if (!c.key) throw new Error("cle absente");
    const imprime = String(c.rawTitle || ""), sousImprime = String(c.rawSubTitle || "");
    return {
      cle: c.key, sku: donnees.labelSku, html: c.html || "",
      titreImprime: imprime, titreCoupe: (titre || "") !== imprime,
      sousTitreImprime: sousImprime, sousTitreCoupe: (sousTitre || "") !== sousImprime,
      titreMise: String(c.formattedTitle || "").replace(/<br\s*\/?>/gi, "\n"),
      proprietes: {          // ce que la commande devra porter, comme une commande du site
        _customTitle: c.rawTitle || "", _customText: c.rawSubTitle || "", _customTitleSize: c.titleSizeClass || "",
        _customTopMargin: c.topMarginClass || "", _customUnderTitleMargin: c.underTitleMarginClass || "",
        _customUnderSubTitleMargin: c.underSubTitleMarginClass || "", _imageHash: empreinte || ""
      }
    };
  }

  function urlApercu(skuPlante, skuPot, cle) {
    return `${HOTE}/${VERSION}/label/preview/${encodeURIComponent(skuPlante)}-${encodeURIComponent(skuPot)}/${cle}?lang=fr&withoutBackground=true`;
  }

  // l'apercu du serveur est un calque de la taille d'une photo produit : on n'en garde que l'etiquette
  function recadrer(url) {
    return new Promise((ok, ko) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      const minuteur = setTimeout(() => ko(new Error("delai")), DELAI);
      img.onload = () => {
        clearTimeout(minuteur);
        const c = document.createElement("canvas");
        c.width = img.naturalWidth; c.height = img.naturalHeight;
        const x = c.getContext("2d"); x.drawImage(img, 0, 0);
        const px = x.getImageData(0, 0, c.width, c.height).data;
        let x0 = c.width, y0 = c.height, x1 = -1, y1 = -1;
        for (let y = 0; y < c.height; y += 2) for (let i = 0; i < c.width; i += 2) {
          if (px[(y * c.width + i) * 4 + 3] > 8) { if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y; }
        }
        if (x1 < 0) return ko(new Error("apercu vide"));
        const m = 6, l = Math.min(c.width, x1 + m) - Math.max(0, x0 - m), h = Math.min(c.height, y1 + m) - Math.max(0, y0 - m);
        const d = document.createElement("canvas"); d.width = l; d.height = h;
        d.getContext("2d").drawImage(c, Math.max(0, x0 - m), Math.max(0, y0 - m), l, h, 0, 0, l, h);
        ok(d.toDataURL("image/png"));
      };
      img.onerror = () => { clearTimeout(minuteur); ko(new Error("apercu indisponible")); };
      img.src = url;
    });
  }

  async function apercu({ taille, titre, sousTitre, empreinte, skuPlante, skuPot }) {
    const page = await mettreEnPage({ taille, titre, sousTitre, empreinte });
    const image = await recadrer(urlApercu(skuPlante, skuPot, page.cle));
    return Object.assign(page, { image });
  }

  // le calque de l'etiquette, a poser sur la photo produit de la meme plante dans le meme pot
  async function calque({ taille, titre, sousTitre, empreinte, type, skuPlante, skuPot }) {
    const page = await mettreEnPage({ taille, titre, sousTitre, empreinte, type });
    const url = urlApercu(skuPlante, skuPot, page.cle);
    await new Promise((ok, ko) => {                   // precharge : pas d'image vide a l'ecran
      const img = new Image(); img.crossOrigin = "anonymous";
      const minuteur = setTimeout(() => ko(new Error("delai")), DELAI);
      img.onload = () => { clearTimeout(minuteur); ok(); };
      img.onerror = () => { clearTimeout(minuteur); ko(new Error("apercu indisponible")); };
      img.src = url;
    });
    return Object.assign(page, { url });
  }

  // photo + calque en une seule image, jointe a la demande pour l'equipe
  function composer(photo, urlCalque, largeur) {
    return new Promise((ok, ko) => {
      const a = new Image(), b = new Image(); let n = 0;
      a.crossOrigin = b.crossOrigin = "anonymous";
      const pret = () => {
        if (++n < 2) return;
        const l = largeur || 900, h = Math.round(l * b.naturalHeight / b.naturalWidth);
        const c = document.createElement("canvas"); c.width = l; c.height = h;
        const x = c.getContext("2d"); x.fillStyle = "#fff"; x.fillRect(0, 0, l, h);
        x.drawImage(a, 0, 0, l, h); x.drawImage(b, 0, 0, l, h);
        try { ok(c.toDataURL("image/jpeg", 0.88)); } catch (e) { ko(e); }
      };
      a.onload = b.onload = pret; a.onerror = b.onerror = () => ko(new Error("image"));
      a.src = photo; b.src = urlCalque;
    });
  }

  return { deposerLogo, mettreEnPage, apercu, calque, composer, urlApercu, skuEtiquette, aplatir };
});
